<script setup lang="ts">
import { computed, defineEmits } from 'vue';
import FaAngleLeft from '@/components/icons/FaAngleLeft.vue';
import FaAngleRight from '@/components/icons/FaAngleRight.vue';
import { usePaginationCustomisation } from '../composables/usePaginationCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';

interface Props {
  total: number;
  current: number;
  limit: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'change-page', page: number): void;
}>();

const customisation = usePaginationCustomisation();

const totalPages = computed(() => {
  return Math.ceil(props.total / props.limit);
});

const hasPrev = computed(() => {
  return props.current > 1;
});

const hasNext = computed(() => {
  return props.current < totalPages.value;
});

const textStyle = computed(() => SUtils.createTextStyle(customisation.textColor));

const activeIconStyle = computed(() => SUtils.createTextStyle(customisation.activeIconColor));

const disabledIconStyle = computed(() => SUtils.createTextStyle(customisation.disabledIconColor));

const handlePrev = () => {
  if (hasPrev.value) {
    emit('change-page', props.current - 1);
  }
};

const handleNext = () => {
  if (hasNext.value) {
    emit('change-page', props.current + 1);
  }
};
</script>

<template>
  <div v-if="totalPages > 1" class="conversations-pagination">
    <button
      :class="['pagination-button', { 'is-disabled': !hasPrev }]"
      :disabled="!hasPrev"
      aria-label="Previous page"
      @click="handlePrev"
    >
      <FaAngleLeft class="pagination-icon" :style="hasPrev ? activeIconStyle : disabledIconStyle" />
    </button>
    <span class="theme-font-main" :style="textStyle"> {{ current }} / {{ totalPages }} </span>
    <button
      :class="['pagination-button', { 'is-disabled': !hasNext }]"
      :disabled="!hasNext"
      aria-label="Next page"
      @click="handleNext"
    >
      <FaAngleRight class="pagination-icon" :style="hasNext ? activeIconStyle : disabledIconStyle" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.conversations-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
}

.pagination-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px;

  &.is-disabled {
    cursor: not-allowed;
  }
}

.pagination-icon {
  width: 24px;
  height: 24px;
}
</style>
